import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { T, rgba } from '../../utils/adminTheme';

// ─── Typen ────────────────────────────────────────────────────────────────
interface CustomerData {
  id: number;
  name: string;
  email: string;
  phone?: string;
  notes?: string;
  service_durations?: Record<string, number>;
  custom_duration?: number;
  is_regular?: boolean;
}

interface Booking {
  id: number;
  booking_date: string;
  booking_time: string;
  status: string;
  services?: { name: string; duration: number };
  locations?: { name: string };
}

const firstRelation = <T,>(value: T | T[] | null | undefined): T | undefined => {
  if (Array.isArray(value)) return value[0];
  return value ?? undefined;
};

// ─── Styles ───────────────────────────────────────────────────────────────
const input: React.CSSProperties = {
  width: '100%', padding: '11px 14px', boxSizing: 'border-box',
  background: T.surface2, border: `1px solid ${T.line}`,
  borderRadius: T.r.md, fontFamily: T.body, fontSize: 14,
  color: T.text, outline: 'none',
};
const label: React.CSSProperties = {
  display: 'block', fontFamily: T.body, fontSize: 11, fontWeight: 600,
  color: T.muted, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em',
};

function statusLabel(s: string) {
  const map: Record<string, { label: string; color: string }> = {
    confirmed: { label: 'Bestätigt', color: T.ok },
    pending:   { label: 'Offen',     color: T.warn },
    cancelled: { label: 'Storniert', color: T.danger },
    completed: { label: 'Erledigt',  color: T.accent },
    'no-show': { label: 'No-Show',   color: T.faint },
  };
  return map[s] ?? { label: s, color: T.muted };
}

// ─── Login / Registrierung ────────────────────────────────────────────────
function AuthForm({ onSuccess }: { onSuccess: () => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handle = async () => {
    setError(''); setInfo(''); setLoading(true);
    try {
      if (mode === 'login') {
        const { error: e } = await supabase.auth.signInWithPassword({ email, password });
        if (e) throw e;
        onSuccess();
      } else {
        const { error: e } = await supabase.auth.signUp({ email, password });
        if (e) throw e;
        // Kundenprofil anlegen falls noch nicht vorhanden
        const { data: existing } = await supabase.from('customers').select('id').eq('email', email).maybeSingle();
        if (!existing) {
          await supabase.from('customers').insert({ name: name || email.split('@')[0], email, phone: '' });
        }
        setInfo('Bitte bestätige deine E-Mail, dann kannst du dich einloggen.');
        setMode('login');
      }
    } catch (e: any) {
      setError(e.message || 'Fehler. Bitte nochmal versuchen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: T.r.lg, padding: '32px 28px', maxWidth: 400, width: '100%', boxShadow: `0 20px 60px ${rgba(T.text, 0.08)}` }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>✿</div>
          <h1 style={{ margin: 0, fontFamily: T.display, fontStyle: 'italic', fontSize: 22, color: T.text }}>Mein Konto</h1>
          <p style={{ margin: '4px 0 0', fontFamily: T.body, fontSize: 13, color: T.muted }}>
            {mode === 'login' ? 'Melde dich an um deine Termine zu sehen' : 'Erstelle dein persönliches Konto'}
          </p>
        </div>

        {error && (
          <div style={{ background: rgba('#dc2626', 0.08), border: `1px solid ${rgba('#dc2626', 0.25)}`, borderRadius: T.r.sm, padding: '9px 12px', marginBottom: 16, fontFamily: T.body, fontSize: 13, color: '#dc2626' }}>
            {error}
          </div>
        )}
        {info && (
          <div style={{ background: rgba(T.ok, 0.08), border: `1px solid ${rgba(T.ok, 0.25)}`, borderRadius: T.r.sm, padding: '9px 12px', marginBottom: 16, fontFamily: T.body, fontSize: 13, color: T.ok }}>
            {info}
          </div>
        )}

        <div style={{ display: 'grid', gap: 14 }}>
          {mode === 'register' && (
            <div>
              <label style={label}>Name</label>
              <input style={input} value={name} onChange={e => setName(e.target.value)} placeholder="Dein Name" />
            </div>
          )}
          <div>
            <label style={label}>E-Mail</label>
            <input style={input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="deine@email.ch" />
          </div>
          <div>
            <label style={label}>Passwort</label>
            <input style={input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
              onKeyDown={e => e.key === 'Enter' && handle()} />
          </div>
          <button
            onClick={handle}
            disabled={loading || !email || !password}
            style={{
              width: '100%', padding: '13px', background: T.accent, border: 'none',
              borderRadius: T.r.md, fontFamily: T.body, fontSize: 15, fontWeight: 600,
              color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
              opacity: (!email || !password) ? 0.5 : 1,
            }}
          >
            {loading ? 'Einen Moment…' : mode === 'login' ? 'Anmelden' : 'Konto erstellen'}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); setInfo(''); }}
            style={{ background: 'none', border: 'none', fontFamily: T.body, fontSize: 13, color: T.accent, cursor: 'pointer', textDecoration: 'underline' }}>
            {mode === 'login' ? 'Noch kein Konto? Registrieren' : 'Bereits registriert? Anmelden'}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <a href="/" style={{ fontFamily: T.body, fontSize: 12, color: T.muted, textDecoration: 'none' }}>
            ← Zurück zur Buchung
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────
function Dashboard({ user, onLogout }: { user: any; onLogout: () => void }) {
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [services, setServices] = useState<{ id: number; name: string; duration: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'bookings' | 'profile'>('bookings');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [custRes, bkRes, svcRes] = await Promise.all([
        supabase.from('customers').select('*').eq('email', user.email).maybeSingle(),
        supabase.from('bookings')
          .select('id,booking_date,booking_time,status,services(name,duration),locations(name)')
          .eq('customer_email', user.email)
          .order('booking_date', { ascending: false })
          .limit(20),
        supabase.from('services').select('id,name,duration').order('name'),
      ]);
      setCustomer(custRes.data);
      const normalizedBookings = (bkRes.data || []).map((booking: any): Booking => ({
        id: Number(booking.id),
        booking_date: booking.booking_date,
        booking_time: booking.booking_time,
        status: booking.status,
        services: firstRelation(booking.services),
        locations: firstRelation(booking.locations),
      }));
      setBookings(normalizedBookings);
      setServices(svcRes.data || []);
      setLoading(false);
    })();
  }, [user.email]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: T.body, color: T.muted }}>Lädt…</div>
    </div>
  );

  const upcoming = bookings.filter(b => b.booking_date >= new Date().toISOString().slice(0, 10) && b.status !== 'cancelled');
  const past     = bookings.filter(b => b.booking_date < new Date().toISOString().slice(0, 10) || b.status === 'cancelled');

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: T.body }}>
      {/* Header */}
      <div style={{ background: T.surface, borderBottom: `1px solid ${T.line}`, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>✿</span>
          <div>
            <div style={{ fontFamily: T.display, fontStyle: 'italic', fontSize: 18, color: T.text }}>Mein Konto</div>
            <div style={{ fontSize: 11, color: T.muted }}>{customer?.name || user.email}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <a href="/" style={{ fontFamily: T.body, fontSize: 13, color: T.accent, textDecoration: 'none', padding: '6px 12px', border: `1px solid ${T.accentSoft ?? T.line}`, borderRadius: T.r.sm }}>
            + Neuer Termin
          </a>
          <button onClick={onLogout} style={{ background: 'none', border: 'none', fontFamily: T.body, fontSize: 12, color: T.muted, cursor: 'pointer' }}>Abmelden</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, background: T.surface, borderBottom: `1px solid ${T.line}`, padding: '0 20px' }}>
        {(['bookings', 'profile'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '12px 16px', background: 'none', border: 'none',
            borderBottom: tab === t ? `2px solid ${T.accent}` : '2px solid transparent',
            color: tab === t ? T.text : T.muted, fontFamily: T.body, fontSize: 13, fontWeight: tab === t ? 600 : 400, cursor: 'pointer',
          }}>
            {{ bookings: 'Meine Termine', profile: 'Profil & Zeiten' }[t]}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 16px' }}>

        {/* ── Termine ── */}
        {tab === 'bookings' && (
          <div style={{ display: 'grid', gap: 20 }}>
            {upcoming.length > 0 && (
              <section>
                <h2 style={{ margin: '0 0 12px', fontFamily: T.display, fontStyle: 'italic', fontSize: 20, color: T.text }}>Kommende Termine</h2>
                <div style={{ display: 'grid', gap: 10 }}>
                  {upcoming.map(b => (
                    <BookingCard key={b.id} booking={b}
                      onCancel={id => setBookings(prev => prev.map(x => x.id === id ? { ...x, status: 'cancelled' } : x))}
                    />
                  ))}
                </div>
              </section>
            )}
            {past.length > 0 && (
              <section>
                <h2 style={{ margin: '0 0 12px', fontFamily: T.display, fontStyle: 'italic', fontSize: 20, color: T.text, opacity: 0.6 }}>Vergangene Termine</h2>
                <div style={{ display: 'grid', gap: 10 }}>
                  {past.slice(0, 5).map(b => <BookingCard key={b.id} booking={b} muted />)}
                </div>
              </section>
            )}
            {bookings.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📅</div>
                <div style={{ fontFamily: T.body, fontSize: 15, color: T.muted }}>Noch keine Termine</div>
                <a href="/" style={{ display: 'inline-block', marginTop: 16, padding: '10px 20px', background: T.accent, color: '#fff', borderRadius: T.r.md, fontFamily: T.body, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                  Jetzt buchen
                </a>
              </div>
            )}
          </div>
        )}

        {/* ── Profil & Zeiten ── */}
        {tab === 'profile' && (
          <div style={{ display: 'grid', gap: 20 }}>
            {/* Persönliche Infos */}
            <section style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: T.r.lg, padding: 20 }}>
              <h2 style={{ margin: '0 0 16px', fontFamily: T.display, fontStyle: 'italic', fontSize: 20, color: T.text }}>Meine Daten</h2>
              <div style={{ display: 'grid', gap: 10 }}>
                {[
                  { label: 'Name', value: customer?.name },
                  { label: 'E-Mail', value: customer?.email },
                  { label: 'Telefon', value: customer?.phone || '—' },
                ].map(({ label: l, value: v }) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.lineSoft}` }}>
                    <span style={{ fontSize: 13, color: T.muted }}>{l}</span>
                    <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{v}</span>
                  </div>
                ))}
              </div>
              <p style={{ margin: '12px 0 0', fontSize: 11, color: T.muted }}>
                Für Änderungen kontaktiere uns bitte direkt.
              </p>
            </section>

            {/* Persönliche Behandlungszeiten */}
            {services.length > 0 && customer?.service_durations && Object.keys(customer.service_durations).length > 0 && (
              <section style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: T.r.lg, padding: 20 }}>
                <h2 style={{ margin: '0 0 6px', fontFamily: T.display, fontStyle: 'italic', fontSize: 20, color: T.text }}>Meine Behandlungszeiten</h2>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: T.muted }}>Dein Salon hat für dich individuelle Zeiten hinterlegt.</p>
                <div style={{ display: 'grid', gap: 8 }}>
                  {services.map(svc => {
                    const custom = customer?.service_durations?.[String(svc.id)];
                    if (!custom) return null;
                    const diff = custom - svc.duration;
                    return (
                      <div key={svc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: T.surface2, borderRadius: T.r.sm }}>
                        <span style={{ fontFamily: T.body, fontSize: 13, color: T.text }}>{svc.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12, color: T.muted, textDecoration: 'line-through' }}>{svc.duration} Min.</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: diff < 0 ? T.ok : diff > 0 ? T.warn : T.text }}>
                            {custom} Min.
                          </span>
                          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: diff < 0 ? rgba(T.ok, 0.12) : rgba(T.warn, 0.12), color: diff < 0 ? T.ok : T.warn }}>
                            {diff < 0 ? `${diff} Min.` : `+${diff} Min.`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {customer?.is_regular && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: rgba(T.accent, 0.07), border: `1px solid ${rgba(T.accent, 0.2)}`, borderRadius: T.r.md }}>
                <span style={{ fontSize: 18 }}>⭐</span>
                <span style={{ fontFamily: T.body, fontSize: 13, color: T.text, fontWeight: 500 }}>Du bist ein Stammkunde — danke für deine Treue!</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── iCal Export ─────────────────────────────────────────────────────────
function downloadIcal(booking: Booking) {
  const svc = (booking.services as any);
  const loc = (booking.locations as any);
  const start = new Date(`${booking.booking_date}T${booking.booking_time}`);
  const end   = new Date(start.getTime() + (svc?.duration ?? 60) * 60000);
  const fmt   = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const ical  = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//TraumBeauty//DE',
    'BEGIN:VEVENT',
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${svc?.name ?? 'Termin'} – Traum Beauty`,
    `LOCATION:${loc?.name ?? ''}`,
    `DESCRIPTION:Dein Termin bei Traum Beauty Cakar`,
    `STATUS:CONFIRMED`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ical], { type: 'text/calendar' });
  const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `termin-${booking.id}.ics` });
  a.click();
}

// ─── Buchungskarte ────────────────────────────────────────────────────────
function BookingCard({ booking, muted, onCancel }: {
  booking: Booking; muted?: boolean;
  onCancel?: (id: number) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const st = statusLabel(booking.status);
  const date = new Date(booking.booking_date + 'T12:00').toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  // Stornierung nur bis 24h vorher möglich
  const appointmentDt = new Date(`${booking.booking_date}T${booking.booking_time}`);
  const canCancel = !muted && booking.status !== 'cancelled' && booking.status !== 'completed'
    && (appointmentDt.getTime() - Date.now()) > 24 * 60 * 60 * 1000;

  const doCancel = async () => {
    setCancelling(true);
    await supabase.from('bookings').update({ status: 'cancelled' }).eq('id', booking.id);
    onCancel?.(booking.id);
    setCancelling(false);
    setConfirming(false);
  };

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: T.r.md, padding: '14px 18px', opacity: muted ? 0.65 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          <div style={{ fontFamily: T.body, fontSize: 14, fontWeight: 600, color: T.text }}>{(booking.services as any)?.name ?? '—'}</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>
            {date} · {booking.booking_time.slice(0, 5)} Uhr · {(booking.locations as any)?.name ?? '—'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!muted && (
            <button onClick={() => downloadIcal(booking)} title="Zum Kalender hinzufügen"
              style={{ background: 'none', border: `1px solid ${T.line}`, borderRadius: T.r.sm, padding: '4px 8px', fontSize: 12, color: T.muted, cursor: 'pointer', fontFamily: T.body }}>
              📅
            </button>
          )}
          <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999, background: rgba(st.color, 0.12), color: st.color, whiteSpace: 'nowrap' }}>
            {st.label}
          </span>
        </div>
      </div>

      {/* Stornierung */}
      {canCancel && !confirming && (
        <button onClick={() => setConfirming(true)}
          style={{ marginTop: 10, background: 'none', border: 'none', fontFamily: T.body, fontSize: 12, color: T.muted, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
          Termin stornieren
        </button>
      )}
      {confirming && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: T.body, fontSize: 12, color: '#dc2626' }}>Wirklich stornieren?</span>
          <button onClick={doCancel} disabled={cancelling}
            style={{ padding: '4px 12px', background: '#dc2626', border: 'none', borderRadius: T.r.sm, fontFamily: T.body, fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
            {cancelling ? '…' : 'Ja, stornieren'}
          </button>
          <button onClick={() => setConfirming(false)}
            style={{ padding: '4px 10px', background: T.surface2, border: `1px solid ${T.line}`, borderRadius: T.r.sm, fontFamily: T.body, fontSize: 12, color: T.muted, cursor: 'pointer' }}>
            Nein
          </button>
        </div>
      )}
      {canCancel === false && !muted && booking.status !== 'cancelled' && (
        <div style={{ marginTop: 8, fontSize: 11, color: T.muted, fontFamily: T.body }}>
          Stornierung nur bis 24h vor dem Termin möglich.
        </div>
      )}
    </div>
  );
}

// ─── Haupt-Export ─────────────────────────────────────────────────────────
const CustomerPortal: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    sessionStorage.removeItem('adminView');
    sessionStorage.removeItem('adminTab');
    sessionStorage.removeItem('adminLoginIntent');
    setUser(null);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: T.body, color: T.muted, fontSize: 14 }}>Lädt…</div>
    </div>
  );

  if (!user) return <AuthForm onSuccess={() => supabase.auth.getSession().then(({ data }) => setUser(data.session?.user ?? null))} />;
  return <Dashboard user={user} onLogout={logout} />;
};

export default CustomerPortal;
